import { message, Select } from "antd";
import { Button as ButtonFlowBite, ButtonGroup } from "flowbite-react";
import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { api } from "../../../api";
import { PageHeading } from "../../../externalComponents";
import MultiTagAutocomplete from "../../../externalComponents/flow_editor/shared/panel/components/MultiTagAutocomplete";
import TagDisplay from "../../../externalComponents/flow_editor/shared/panel/components/TagDisplay";
import { Button } from "../../../externalComponents/tailwind/button";
import { Field, Label } from "../../../externalComponents/tailwind/fieldset";
import { Input } from "../../../externalComponents/tailwind/input";
import { CustomerContext } from "../../../providers";

const ContactsImportBrevoPage = () => {
  const { user, customer } = useContext(CustomerContext);
  const navigate = useNavigate();

  const [isImporting, setIsImporting] = useState(false);
  const [isImported, setIsImported] = useState(false);
  const [loadingSegments, setLoadingSegments] = useState(true);

  const [segments, setSegments] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [shouldOptinContacts, setShouldOptinContacts] = useState(null);
  const [autoTagName, setAutoTagName] = useState(
    `Brevo Import - ${new Date().toISOString().replace("T", " ").slice(0, 19)} UTC`,
  );
  const [additionalTags, setAdditionalTags] = useState([]);

  const buttonClassList =
    "flex-1 border-[1px] border-gray-300 bg-transparent text-gray-800 hover:bg-gray-200 hover:text-gray-900 transition duration-200";

  useEffect(() => {
    const fetchSegments = async () => {
      try {
        setLoadingSegments(true);
        const result = await api.brevo.getSegments();
        setSegments(result.segments || []);
      } catch (error) {
        message.error("Failed to load Brevo segments");
        console.error(error);
      } finally {
        setLoadingSegments(false);
      }
    };

    if (customer?.id) {
      fetchSegments();
    }
  }, [customer?.id]);

  useEffect(() => {
    if (selectedSegment) {
      const segment = segments.find((s) => s.id === selectedSegment);
      if (segment) {
        setAutoTagName(
          `Brevo Import - ${segment.segmentName} - ${new Date().toISOString().replace("T", " ").slice(0, 19)} UTC`,
        );
      }
    }
  }, [selectedSegment, segments]);

  return (
    <>
      <PageHeading title="Import contacts from Brevo" />
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: "40vw",
            height: "25vh",
          }}
        >
          <br />

          <Field className={"mt-4"}>
            <Label>Brevo Segment</Label>
            <Select
              showSearch
              placeholder="Select a Brevo segment"
              loading={loadingSegments}
              disabled={isImporting || loadingSegments}
              value={selectedSegment}
              onChange={setSelectedSegment}
              style={{ width: "100%" }}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={segments.map((segment) => ({
                value: segment.id,
                label: `${segment.segmentName}`,
              }))}
            />
          </Field>

          <Field className="mt-4 flex flex-col">
            <Label>New contacts opt-in status</Label>
            <ButtonGroup className="w-full">
              <ButtonFlowBite
                onClick={() => setShouldOptinContacts(true)}
                type="button"
                color="alternative"
                className={
                  buttonClassList +
                  (shouldOptinContacts && " bg-gray-200 text-gray-900")
                }
              >
                Opted in
              </ButtonFlowBite>
              <ButtonFlowBite
                onClick={() => setShouldOptinContacts(false)}
                type="button"
                color="alternative"
                className={
                  buttonClassList +
                  (shouldOptinContacts === false &&
                    " bg-gray-200 text-gray-900")
                }
              >
                Not opted in
              </ButtonFlowBite>
            </ButtonGroup>
          </Field>

          <Field className={"mt-4"}>
            <Label>Auto Tag</Label>
            <Input disabled value={autoTagName} />
          </Field>

          <Field className={"mt-4"}>
            <Label>Additional Tags (Optional)</Label>
            <MultiTagAutocomplete
              selectedTags={additionalTags}
              onTagsChange={setAdditionalTags}
              customerId={user.customerId}
            />
            <TagDisplay
              tags={additionalTags}
              onRemoveTag={(tagId) => {
                setAdditionalTags(
                  additionalTags.filter((tag) => tag.id !== tagId),
                );
              }}
            />
          </Field>

          <Button
            color="primaryButton"
            className="flex w-full justify-center bg-yellow font-title font-bold leading-6 mt-4"
            disabled={
              !selectedSegment ||
              isImporting ||
              isImported ||
              shouldOptinContacts === null
            }
            loading={isImporting}
            onClick={async () => {
              try {
                setIsImporting(true);
                const segment = segments.find((s) => s.id === selectedSegment);
                const newAutoTagName = `Brevo Import - ${segment.segmentName} - ${new Date().toISOString().replace("T", " ").slice(0, 19)} UTC`;
                setAutoTagName(newAutoTagName);

                const autoTag = await api.tag.create({
                  customer: { _link: user.customerId },
                  name: newAutoTagName,
                });

                await api.csvImport.create({
                  customer: { _link: user.customerId },
                  status: "pending",
                  objectType: "contact",
                  tag: { _link: autoTag.id },
                  shouldOptinContacts,
                  source: "brevo",
                  externalId: selectedSegment.toString(),
                  additionalTags:
                    additionalTags.length > 0
                      ? additionalTags.map((tag) => tag.id)
                      : null,
                });

                const successMessage =
                  additionalTags.length > 0
                    ? `Brevo segment "${segment.segmentName}" successfully queued for import with ${additionalTags.length + 1} tags. Treatment in progress.`
                    : `Brevo segment "${segment.segmentName}" successfully queued for import. Treatment in progress.`;

                message.success(successMessage);
                setIsImporting(false);
                setIsImported(true);
                navigate("/settings/data/imports");
              } catch (error) {
                message.error(`Import could not be started`);
                console.log(error);
                setIsImporting(false);
              }
            }}
          >
            Import now
          </Button>
        </div>
      </div>
    </>
  );
};
export default ContactsImportBrevoPage;
